import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import {
  getInsuranceNeedsOpsSummary,
  getInsuranceProtectLandlords,
} from "../context/functions/AdminFunction";
import InsuranceProtectLandlordsTable from "./insurance-protect-metrics/components/InsuranceProtectLandlordsTable";
import InsuranceProtectDetailsModal from "./insurance-protect-metrics/components/InsuranceProtectDetailsModal";
import NeedsOpsSummaryModal from "./insurance-protect-metrics/components/NeedsOpsSummaryModal";
import "../styles/landlord-metrics.css";
import "../styles/tenants.css";
import { constants } from "../utils/constants";

const LIMIT = constants.LISTING_ROWLIMIT;

const InsuranceProtectMetrics = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Deep link from Ops emails: ?landlordId=<id> opens that landlord's cases.
  const [selectedLandlord, setSelectedLandlord] = useState(() => {
    const landlordId = new URLSearchParams(window.location.search).get(
      "landlordId"
    );
    return landlordId ? { landlord_id: landlordId } : null;
  });
  const [showModal, setShowModal] = useState(() => Boolean(selectedLandlord));
  // Opens the landlord's cases with the "Needs ops action" filter on.
  const [openWithNeedsOps, setOpenWithNeedsOps] = useState(false);

  // "Needs ops action" across all landlords (null while loading).
  const [needsOpsSummary, setNeedsOpsSummary] = useState(null);
  const [showNeedsOpsModal, setShowNeedsOpsModal] = useState(false);

  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const fetchData = useCallback(async (page, search) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const res = await getInsuranceProtectLandlords({
        page,
        limit: LIMIT,
        searchText: search,
        signal: controller.signal,
      });

      if (!res || res.status === "canceled") return;

      if (res.success && res.success_res) {
        setData(res.success_res.data || []);
        setTotal(res.success_res.total || 0);
      } else {
        setData([]);
        setTotal(0);
      }
    } catch (err) {
      if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
        toast.error("Failed to load insurance & Skor Protect metrics");
      }
    } finally {
      // A cancelled request must not end the loading of the newer one.
      if (abortControllerRef.current === controller) setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(debounceTimerRef.current);
  }, [searchText]);

  useEffect(() => {
    fetchData(currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch, fetchData]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  useEffect(() => {
    document.title = "Insurance & Skor Protect Metrics | Admin";
  }, []);

  const fetchNeedsOpsSummary = useCallback(
    () =>
      getInsuranceNeedsOpsSummary().then((res) =>
        setNeedsOpsSummary(
          res?.success && res.success_res
            ? res.success_res
            : { total: 0, byLandlord: {}, workspaces: [] }
        )
      ),
    []
  );

  useEffect(() => {
    fetchNeedsOpsSummary();
  }, [fetchNeedsOpsSummary]);

  const openLandlordCases = (landlord, { needsOps = false } = {}) => {
    setOpenWithNeedsOps(needsOps);
    setSelectedLandlord(landlord);
    setShowModal(true);
  };

  return (
    <div className="landlord-metrics-container">
      <div className="d-flex justify-content-between align-items-start mb-4 gap-3 flex-wrap">
        <div className="mb-2">
          <h4 className="fw-bold mb-1" style={{ color: "#2c3e50", fontSize: "22px" }}>
            Insurance & Skor Protect Metrics
          </h4>
          <p className="text-muted small fw-medium mb-0">
            Landlords with confirmed insurances and/or Skor Protect activations across their properties
          </p>
        </div>
        <button
          type="button"
          className="ip-stat-card ip-stat-card-ops ip-needs-ops-total"
          onClick={() => setShowNeedsOpsModal(true)}
          title="See pending Ops actions per workspace"
        >
          <span className="ip-stat-label">Needs ops action</span>
          <span className="ip-stat-value">
            {needsOpsSummary ? needsOpsSummary.total : "…"}
          </span>
        </button>
      </div>

      <InsuranceProtectLandlordsTable
        data={data}
        loading={loading}
        total={total}
        currentPage={currentPage}
        limit={LIMIT}
        onPageChange={setCurrentPage}
        onViewDetails={(item) => openLandlordCases(item)}
        onViewNeedsOps={(item) => openLandlordCases(item, { needsOps: true })}
        needsOpsByLandlord={needsOpsSummary?.byLandlord || {}}
        searchText={searchText}
        onSearchChange={setSearchText}
      />

      <InsuranceProtectDetailsModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setSelectedLandlord(null);
          setOpenWithNeedsOps(false);
          // Ops may have resolved cases inside: refresh the counts.
          fetchNeedsOpsSummary();
        }}
        selectedLandlord={selectedLandlord}
        initialNeedsOpsFilter={openWithNeedsOps}
      />

      <NeedsOpsSummaryModal
        show={showNeedsOpsModal}
        onHide={() => setShowNeedsOpsModal(false)}
        summary={needsOpsSummary}
        loading={!needsOpsSummary}
        onOpen={(entry) => {
          setShowNeedsOpsModal(false);
          openLandlordCases({
            landlord_id: entry.landlord_id,
            landlord: entry.landlord,
          });
        }}
      />
    </div>
  );
};

export default InsuranceProtectMetrics;
